import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import Icon from "@mui/material/Icon";
import IconButton from "@mui/material/IconButton";
import AccessDenied from "components/AccessDenied";
import AlertDialog from "components/AlertDialog";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import TableSearch from "components/TableSearch";
import DataTable from "examples/Tables/DataTable";
import { useEffect, useMemo, useState } from "react";
import userGroupService from "services/user-groups-service";
import UserGroupsEditNewDialog from "./UserGroupsEditNewDialog";
import { useLanguage } from "contexts/language.context";
import useOpen from "utils/useOpen";

export default function AllUserGroups({ permissions, ...props }) {
  const { type = "page", data } = props;

  const { label } = useLanguage();
  const open = useOpen({});

  const [userGroups, setUserGroups] = useState(null);
  const [lastQuery, setLastQuery] = useState({});

  const columns = [
    {
      Header: label("name-singular"),
      accessor: "name",
      width: "25%",
    },
    {
      Header: label("branch-group-plural"),
      accessor: "branchGroups",
      width: "100%",
      bodyCellStyle: {
        whiteSpace: "unset",
        overflow: "unset",
        textOverflow: "unset",
      },
    },
  ];

  if (permissions.update || permissions.delete) {
    columns.push({
      Header: "",
      accessor: "actions",
      align: "right",
      width: "100%",
    });
  }

  async function handleAddUserGroup(newUserGroup) {
    if (props?.data?.accountId) newUserGroup.accountId = props?.data?.accountId;

    let [response, data] = await userGroupService.createUserGroup(
      newUserGroup,
      props.data?.accountId ? { accountId: props.data.accountId } : null
    );
    if (response.ok) {
      [response, data] = await handleGetUserGroups();
      setUserGroups(data);
      return { success: true };
    } else {
      return { error: data.error };
    }
  }

  async function handleUpdateUserGroup(newUserGroup) {
    let [response, data] = await userGroupService.updateUserGroup(
      newUserGroup.id,
      newUserGroup,
      props.data?.accountId ? { accountId: props.data.accountId } : null
    );
    if (response.ok) {
      [response, data] = await handleGetUserGroups();
      setUserGroups(data);
      return { success: true };
    } else {
      return { error: data.error };
    }
  }

  async function handleDeleteUserGroup(id) {
    let [response, data] = await userGroupService.removeUserGroup(
      id,
      props.data?.accountId ? { accountId: props.data.accountId } : null
    );
    if (response.ok) {
      [response, data] = await handleGetUserGroups();
      setUserGroups(data);
      return { success: true };
    } else {
      return { error: data.error };
    }
  }

  async function handleGetUserGroups(query) {
    // if (!query || lastQuery) return;
    return await userGroupService.getUserGroups(query || lastQuery);
  }

  useEffect(() => {
    if (!props?.data?.userGroups) {
      return;
    }

    setUserGroups(props.data.userGroups);
  }, [props.data]);

  const rows = useMemo(() => {
    return userGroups?.length > 0
      ? userGroups.map((userGroup) => {
          return {
            name: (
              <MDTypography variant="inherit">{userGroup.name}</MDTypography>
            ),
            branchGroups: data.branchGroups
              ?.map((branchGroup) => {
                const v = userGroup.branchGroups.find(
                  (b) => branchGroup.id == b.id
                );
                if (v?.internal || v?.external) {
                  return `${branchGroup.name} (${[v.internal ? label("primary-auditor") : "", v.external ? label("secondary-auditor") : ""].filter(Boolean).join(" - ")})`;
                }
              })
              .filter(Boolean)
              .join(" - "),
            actions: (
              <MDBox
                display="flex"
                flexDirection="row"
                justifyContent="end"
                alignItems="center"
              >
                {permissions.update && (
                  <IconButton
                    size="small"
                    onClick={() => {
                      open.change("editDialog", {
                        open: Math.random(),
                        userGroup,
                      });
                    }}
                  >
                    <Icon>edit</Icon>
                  </IconButton>
                )}
                {permissions.delete && (
                  <IconButton
                    size="small"
                    onClick={() => {
                      open.change("alertDialog", {
                        open: Math.random(),
                        userGroup,
                      });
                    }}
                  >
                    <Icon>delete</Icon>
                  </IconButton>
                )}
              </MDBox>
            ),
          };
        })
      : [
          {
            name: label("no-user-groups-were-found"),
          },
        ];
  }, [userGroups]);

  return (
    <>
      <AlertDialog
        open={open.get("alertDialog")?.open}
        setOpen={(v) => {
          open.change("alertDialog", { open: v });
        }}
        title={label("delete-alert-title")}
        content={label("delete-alert-message")}
        onOk={async () => {
          await handleDeleteUserGroup(open.get("alertDialog")?.userGroup?.id);
        }}
      />
      <UserGroupsEditNewDialog
        accountId={data?.accountId}
        isOpen={open.get("newDialog")?.open}
        onSubmit={handleAddUserGroup}
        branchGroups={data?.branchGroups}
      />
      <UserGroupsEditNewDialog
        type="edit"
        isOpen={open.get("editDialog")?.open}
        onSubmit={handleUpdateUserGroup}
        data={open.get("editDialog")?.userGroup}
        accountId={data?.accountId}
        branchGroups={data?.branchGroups}
      />

      <MDBox
        sx={
          type === "component"
            ? { pt: 3 }
            : {
                pt: 6,
                pb: 3,
              }
        }
      >
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={type === "component" ? 2 : 3}
                px={2}
                variant="gradient"
                bgColor="info"
                borderRadius="lg"
                coloredShadow="info"
                display="flex"
                flexDirection="row"
                justifyContent="space-between"
                alignItems="center"
              >
                <MDTypography variant="h6" color="white">
                  {label("user-group-plural")}{" "}
                  {permissions.get ? `(${userGroups?.length || 0})` : ""}
                </MDTypography>
                {permissions.create && (
                  <MDButton
                    size={type === "component" ? "small" : "medium"}
                    onClick={() => {
                      open.change("newDialog", { open: Math.random() });
                    }}
                  >
                    <Icon sx={{ fontWeight: "bold" }}>add</Icon>
                    &nbsp;{label("create-new-user-group-button")}
                  </MDButton>
                )}
              </MDBox>
              {permissions.get ? (
                <>
                  {type !== "component" ? null : (
                    <MDBox p={2} mt={2}>
                      <TableSearch
                        internalLoading={!Boolean(userGroups)}
                        id="all-userGroups"
                        fullWidth
                        setData={setUserGroups}
                        onSearch={async (request, callback) => {
                          setLastQuery(
                            props.data?.accountId
                              ? {
                                  accountId: props.data.accountId,
                                  ...request,
                                }
                              : { ...request }
                          );
                          callback(
                            await handleGetUserGroups(
                              props.data?.accountId
                                ? {
                                    accountId: props.data.accountId,
                                    ...request,
                                  }
                                : { ...request }
                            )
                          );
                        }}
                        label={label("search-user-groups-label")}
                      />
                    </MDBox>
                  )}
                  <MDBox mt={type === "component" ? 2 : null}>
                    <DataTable
                      loading={!Boolean(userGroups)}
                      table={{ columns: columns, rows: rows }}
                      isSorted={false}
                      entriesPerPage={false}
                      showTotalEntries={false}
                      noEndBorder
                    />
                  </MDBox>
                </>
              ) : (
                <AccessDenied type="component" />
              )}
            </Card>
          </Grid>
        </Grid>
      </MDBox>
    </>
  );
}
